import { frame } from "../../frameloop"
import { getValueTransition } from "../utils/get-value-transition"
import { resolveTransition } from "../utils/resolve-transition"
import { positionalKeys } from "../../render/utils/keys-position"
import { setTarget } from "../../render/utils/setters"
import { addValueToWillChange } from "../../value/will-change/add-will-change"
import { getOptimisedAppearId } from "../optimized-appear/get-appear-id"
import { animateMotionValue } from "./motion-value"
import type { MotionPath } from "../types"
import type { VisualElementAnimationOptions } from "./types"
import type { AnimationPlaybackControlsWithThen } from "../types"
import type { TargetAndTransition } from "../../node/types"
import type { AnimationTypeState } from "../../render/utils/animation-state"
import type { VisualElement } from "../../render/VisualElement"

const isBrowser = typeof window !== "undefined"

/**
 * Decide whether we should block this animation. Previously, we achieved this
 * just by checking whether the key was listed in protectedKeys, but this
 * posed problems if an animation was triggered by afterChildren and protectedKeys
 * had been set to true in the meantime.
 */
function shouldBlockAnimation(
    { protectedKeys, needsAnimating }: AnimationTypeState,
    key: string
) {
    const shouldBlock =
        protectedKeys.hasOwnProperty(key) && needsAnimating[key] !== true

    needsAnimating[key] = false
    return shouldBlock
}

export function animateTarget(
    visualElement: VisualElement,
    targetAndTransition: TargetAndTransition,
    { delay = 0, transitionOverride, type }: VisualElementAnimationOptions = {}
): AnimationPlaybackControlsWithThen[] {
    let {
        transition,
        transitionEnd,
        ...target
    } = targetAndTransition

    const defaultTransition = visualElement.getDefaultTransition()
    transition = transition
        ? resolveTransition(transition, defaultTransition)
        : defaultTransition

    const reduceMotion = (transition as { reduceMotion?: boolean })?.reduceMotion
    const skipAnimations = transition?.skipAnimations

    if (transitionOverride) transition = transitionOverride

    const animations: AnimationPlaybackControlsWithThen[] = []

    const animationTypeState =
        type &&
        visualElement.animationState &&
        visualElement.animationState.getState()[type]

    const path = (transition as { path?: MotionPath } | undefined)?.path
    if (path) {
        // path mutates `target` to claim x/y; loop below skips them.
        path.animateVisualElement(
            visualElement,
            target,
            transition,
            delay,
            animations
        )
    }

    for (const key in target) {
        const value = visualElement.getValue(
            key,
            visualElement.latestValues[key] ?? null
        )
        const valueTarget = target[key as keyof typeof target]

        if (
            valueTarget === undefined ||
            (animationTypeState &&
                shouldBlockAnimation(animationTypeState, key))
        ) {
            continue
        }

        const valueTransition = {
            delay,
            ...getValueTransition(transition || {}, key),
        }

        if (skipAnimations) valueTransition.skipAnimations = true

        /**
         * If the value is already at the defined target, skip the animation.
         * We still re-assert the value via frame.update to take precedence
         * over any stale transitionEnd callbacks from previous animations.
         */
        const currentValue = value.get()
        if (
            currentValue !== undefined &&
            !value.isAnimating() &&
            !Array.isArray(valueTarget) &&
            valueTarget === currentValue &&
            !valueTransition.velocity
        ) {
            frame.update(() => value.set(valueTarget as any))
            continue
        }

        /**
         * If this is the first time a value is being animated, check
         * to see if we're handling off from an existing animation.
         */
        let isHandoff = false
        if (isBrowser && window.MotionHandoffAnimation) {
            const appearId = getOptimisedAppearId(visualElement)

            if (appearId) {
                const startTime = window.MotionHandoffAnimation(
                    appearId,
                    key,
                    frame
                )

                if (startTime !== null) {
                    valueTransition.startTime = startTime
                    isHandoff = true
                }
            }
        }

        addValueToWillChange(visualElement, key)

        const shouldReduceMotion =
            reduceMotion ?? visualElement.shouldReduceMotion

        value.start(
            animateMotionValue(
                key,
                value,
                valueTarget,
                shouldReduceMotion && positionalKeys.has(key)
                    ? { type: false }
                    : valueTransition,
                visualElement,
                isHandoff
            )
        )

        const animation = value.animation as
            | AnimationPlaybackControlsWithThen
            | undefined

        if (animation) {
            animations.push(animation)
        }
    }

    if (transitionEnd) {
        const applyTransitionEnd = () =>
            frame.update(() => {
                transitionEnd && setTarget(visualElement, transitionEnd)
            })

        if (animations.length) {
            Promise.all(animations).then(applyTransitionEnd)
        } else {
            applyTransitionEnd()
        }
    }

    return animations
}
